import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import {
  Smartphone,
  Apple,
  MoreVertical,
  Share,
  Plus,
  Download,
  Monitor,
  Info,
} from 'lucide-react';

/**
 * Install App modal:
 * - If beforeinstallprompt was captured (Chromium / Edge desktop / Android Chrome),
 *   the green "Install now" button fires the native browser dialog.
 * - Otherwise the user sees OS-specific manual instructions
 *   (iOS Safari, Android Chrome, Desktop Chrome / Edge).
 */
export const InstallAppModal = ({ open, onOpenChange, deferredPrompt, onInstalled }) => {
  const [os, setOS] = useState('android');

  useEffect(() => {
    if (!open) return;
    const ua = navigator.userAgent.toLowerCase();
    if (/iphone|ipad|ipod/.test(ua)) setOS('ios');
    else if (/android/.test(ua)) setOS('android');
    else setOS('desktop');
  }, [open]);

  const isInstalled =
    typeof window !== 'undefined' &&
    (window.matchMedia?.('(display-mode: standalone)')?.matches ||
      window.navigator?.standalone === true);

  const triggerNative = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') onInstalled?.();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="install-app-modal">
        <DialogHeader>
          <DialogTitle
            className="text-xl tracking-tight"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="install-modal-title"
          >
            Install GateKeeper Pro
          </DialogTitle>
          <DialogDescription
            className="text-slate-600"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
          >
            Add the app to your home screen / desktop for the fastest experience.
          </DialogDescription>
        </DialogHeader>

        {isInstalled && (
          <div
            className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200 text-xs text-emerald-900"
            data-testid="install-already-installed"
          >
            <Info className="w-4 h-4 mt-0.5 shrink-0" />
            <span>
              Looks like the app is already installed on this device. You can launch it from your home screen / dock.
            </span>
          </div>
        )}

        {/* Native prompt — works on Chromium desktop + Android Chrome when criteria are met */}
        {deferredPrompt && (
          <button
            onClick={triggerNative}
            className="w-full bg-[#14D4C6] hover:bg-[#10B5A9] text-slate-900 font-semibold px-5 py-3 rounded-md transition-colors flex items-center justify-center gap-2"
            data-testid="install-native-btn"
          >
            <Download className="w-4 h-4" />
            Install now
          </button>
        )}

        {/* OS Tabs */}
        <div className="flex gap-2 mt-2" data-testid="install-os-tabs">
          <TabButton
            active={os === 'ios'}
            onClick={() => setOS('ios')}
            testid="install-tab-ios"
            icon={<Apple className="w-4 h-4" />}
            label="iOS / Safari"
          />
          <TabButton
            active={os === 'android'}
            onClick={() => setOS('android')}
            testid="install-tab-android"
            icon={<Smartphone className="w-4 h-4" />}
            label="Android"
          />
          <TabButton
            active={os === 'desktop'}
            onClick={() => setOS('desktop')}
            testid="install-tab-desktop"
            icon={<Monitor className="w-4 h-4" />}
            label="Desktop"
          />
        </div>

        {os === 'ios' && (
          <ol
            className="space-y-3 mt-2 text-sm text-slate-700"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
            data-testid="install-steps-ios"
          >
            <Step num="1" icon={<Share className="w-4 h-4" />} text="Tap the Share button at the bottom of Safari." />
            <Step num="2" icon={<Plus className="w-4 h-4" />} text='Scroll and tap "Add to Home Screen".' />
            <Step num="3" icon={<Apple className="w-4 h-4" />} text='Confirm "Add". GateKeeper Pro lives on your home screen.' />
          </ol>
        )}

        {os === 'android' && (
          <ol
            className="space-y-3 mt-2 text-sm text-slate-700"
            style={{ fontFamily: 'Work Sans, sans-serif' }}
            data-testid="install-steps-android"
          >
            <Step num="1" icon={<MoreVertical className="w-4 h-4" />} text="Tap the three-dot menu in Chrome (top-right)." />
            <Step num="2" icon={<Download className="w-4 h-4" />} text='Choose "Install app" or "Add to Home screen".' />
            <Step num="3" icon={<Smartphone className="w-4 h-4" />} text="Confirm. The app installs and opens full-screen." />
          </ol>
        )}

        {os === 'desktop' && (
          <div data-testid="install-steps-desktop" className="space-y-3 mt-2">
            {!deferredPrompt && !isInstalled && (
              <div className="flex items-start gap-2 p-3 rounded-lg bg-slate-50 border border-slate-200 text-xs text-slate-700">
                <Info className="w-4 h-4 mt-0.5 shrink-0 text-slate-500" />
                <span>
                  If the green "Install now" button isn&apos;t showing, your browser
                  hasn&apos;t fired the install prompt yet (Chrome only fires it once it&apos;s
                  confident the site qualifies). Use the manual steps below — they always work.
                </span>
              </div>
            )}
            <ol
              className="space-y-3 text-sm text-slate-700"
              style={{ fontFamily: 'Work Sans, sans-serif' }}
            >
              <Step
                num="1"
                icon={<Monitor className="w-4 h-4" />}
                text='Look at the right edge of the address bar in Chrome / Edge for an "install" icon (a monitor with a down-arrow, or a + symbol).'
              />
              <Step
                num="2"
                icon={<Download className="w-4 h-4" />}
                text='Click it — or open the three-dot menu (⋮) → "Install GateKeeper Pro" / "Apps → Install this site as an app".'
              />
              <Step
                num="3"
                icon={<Plus className="w-4 h-4" />}
                text="Confirm. The app opens in its own window with no browser chrome and adds itself to your dock / Start menu."
              />
            </ol>
            <p className="text-xs text-slate-500 italic">
              Safari on macOS supports it too: File → Add to Dock.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const TabButton = ({ active, onClick, testid, icon, label }) => (
  <button
    onClick={onClick}
    className={`flex-1 py-2 px-2 text-xs sm:text-sm font-medium rounded-md border transition-all flex items-center justify-center gap-1.5 ${
      active
        ? 'bg-slate-900 text-white border-slate-900'
        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
    }`}
    data-testid={testid}
  >
    {icon} {label}
  </button>
);

const Step = ({ num, icon, text }) => (
  <li className="flex items-start gap-3">
    <span className="w-6 h-6 rounded-full bg-[#14D4C6]/20 text-[#0F766E] text-xs font-semibold flex items-center justify-center shrink-0 mt-0.5">
      {num}
    </span>
    <span className="flex items-start gap-2 leading-relaxed">
      <span className="mt-0.5 shrink-0">{icon}</span>
      <span>{text}</span>
    </span>
  </li>
);
